/**
 * The 3Y / 5Y / 7Y / 10Y span picker, with an older/newer pager.
 *
 * Lifted from `FinancialDashboardTab` so a second screen showing many fiscal
 * years does not invent a different control for the same job. The markup and
 * classes are carried over deliberately unchanged — a picker that looks almost
 * like the Finance tab's is worse than one that looks exactly like it.
 *
 * `FinancialDashboardTab` still holds its own copy. Refactoring that file to
 * consume this one is a separate change: it is large, central, and nothing
 * about the tab this was extracted for requires touching it.
 *
 * Two rules the caller does not have to think about:
 *
 *   - A span wider than the data is disabled and struck through rather than
 *     hidden, so a reader can see that ten years is an option this company
 *     cannot fill — and the title says how many it has.
 *   - The offset is CLAMPED here, not stored clamped, so shrinking the span or
 *     switching to a company with fewer years cannot leave the window pointing
 *     past the end of the data.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { YEAR_WINDOW_OPTIONS, clampWindow, type YearWindowSize } from './yearWindow';

export interface YearWindowSelectorProps {
  /** How many years the data actually holds. */
  totalYears: number;
  size: number;
  onSizeChange: (size: YearWindowSize) => void;
  /** How many years back from the newest the window starts. */
  offset: number;
  onOffsetChange: (offset: number) => void;
  /** The first and last year currently on screen, for the pager's label. */
  firstYear?: number | null;
  lastYear?: number | null;
  className?: string;
}

export default function YearWindowSelector({
  totalYears,
  size,
  onSizeChange,
  offset,
  onOffsetChange,
  firstYear,
  lastYear,
  className = '',
}: YearWindowSelectorProps) {
  const { size: windowSize, offset: clampedOffset } = clampWindow(totalYears, size, offset);

  const canGoOlder = clampedOffset < totalYears - windowSize;
  const canGoNewer = clampedOffset > 0;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Only when there is more data than the window can show. */}
      {totalYears > windowSize && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onOffsetChange(Math.min(totalYears - windowSize, clampedOffset + 1))}
            disabled={!canGoOlder}
            className="p-1 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Older years"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <span className="text-xs text-neutral-500 tabular-nums min-w-[5rem] text-center">
            {firstYear && lastYear
              ? `FY${String(firstYear).slice(-2)}–FY${String(lastYear).slice(-2)}`
              : ''}
          </span>
          <button
            type="button"
            onClick={() => onOffsetChange(Math.max(0, clampedOffset - 1))}
            disabled={!canGoNewer}
            className="p-1 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Newer years"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div className="flex items-center gap-0.5 bg-white rounded-lg border border-neutral-200 p-0.5">
        {YEAR_WINDOW_OPTIONS.map((option) => {
          const exceedsAvailable = totalYears > 0 && option > totalYears;
          const isSelected = windowSize === option;

          return (
            <button
              key={option}
              type="button"
              onClick={() => {
                if (exceedsAvailable) return;
                onSizeChange(option);
                onOffsetChange(0);
              }}
              disabled={exceedsAvailable}
              title={
                exceedsAvailable
                  ? `Only ${totalYears} year${totalYears === 1 ? '' : 's'} of data available for this company`
                  : undefined
              }
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                isSelected
                  ? 'bg-primary-600 text-white shadow-sm'
                  : exceedsAvailable
                    ? 'text-neutral-300 cursor-not-allowed line-through'
                    : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100'
              }`}
            >
              {option}Y
            </button>
          );
        })}
      </div>
    </div>
  );
}
